import React, { useEffect, useState, useCallback } from "react";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ddmmyyyy, fileToBase64 } from "../lib/format";
import { SectionTitle, Pill, EmptyState, PrimaryButton, VoiceButton, WhyBanner } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Wrench, Plus, Camera, Loader2, Sparkles, Check } from "lucide-react";

const STAGES = ["reported", "assigned", "in_progress", "resolved"];

export default function Maintenance() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/maintenance").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const isOwner = user?.role === "owner";

  const onPhotos = async (e) => { const b64 = await Promise.all(Array.from(e.target.files).slice(0, 3).map(fileToBase64)); setPhotos((p) => [...p, ...b64].slice(0, 3)); };

  const submit = async () => {
    setBusy(true);
    try { await api.post("/maintenance", { description: desc, photos, language: lang || "en" }); setOpen(false); setDesc(""); setPhotos([]); await load(); }
    catch { alert("Failed"); } finally { setBusy(false); }
  };

  const advance = async (tk) => {
    const idx = STAGES.indexOf(tk.status);
    const next = STAGES[Math.min(idx + 1, STAGES.length - 1)];
    await api.patch(`/maintenance/${tk.id}/status`, { status: next });
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionTitle>{t("maintenance")}</SectionTitle>
        {!isOwner && <button onClick={() => setOpen(true)} data-testid="raise-ticket-open-btn" className="flex items-center gap-1 text-sm font-semibold text-terracotta bg-terracotta-light px-3 py-1.5 rounded-full"><Plus className="w-4 h-4" /> {t("raise_complaint")}</button>}
      </div>

      {items.length === 0 ? <EmptyState icon={Wrench} title={t("maintenance")} sub={t("raise_complaint")} /> : (
        <div className="space-y-3">
          {items.map((tk) => (
            <div key={tk.id} className="gs-card p-4" data-testid={`ticket-${tk.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-slate-800 text-sm flex-1">{tk.description}</div>
                <Pill tone={tk.ai?.urgency === "Emergency" || tk.ai?.urgency === "High" ? "red" : "amber"}>{tk.ai?.urgency}</Pill>
              </div>
              <div className="flex flex-wrap gap-2 mt-2"><Pill tone="slate">{tk.ai?.category}</Pill>{tk.ai?.vendor_type && tk.ai.vendor_type !== "None" && <Pill tone="trust">{tk.ai.vendor_type}</Pill>}</div>
              {tk.ai?.likely_cause && <div className="text-xs text-slate-500 mt-2"><span className="font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3 text-terracotta" /> AI:</span> {tk.ai.likely_cause}. {tk.ai.suggested_action}</div>}
              {tk.photos?.[0] && <img src={tk.photos[0]} alt="" className="w-full h-32 object-cover rounded-xl mt-2" />}
              {/* timeline */}
              <div className="flex items-center gap-1 mt-3">
                {STAGES.map((s, i) => {
                  const done = STAGES.indexOf(tk.status) >= i;
                  return <React.Fragment key={s}><div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? "bg-trust text-white" : "bg-slate-200 text-slate-400"}`}>{done ? <Check className="w-3 h-3" /> : i + 1}</div>{i < STAGES.length - 1 && <div className={`flex-1 h-0.5 ${STAGES.indexOf(tk.status) > i ? "bg-trust" : "bg-slate-200"}`} />}</React.Fragment>;
                })}
              </div>
              <div className="text-[11px] text-slate-400 mt-1 capitalize">{tk.status.replace("_", " ")} · {ddmmyyyy(tk.created_at)}</div>
              {isOwner && tk.status !== "resolved" && <button onClick={() => advance(tk)} data-testid={`advance-${tk.id}`} className="mt-2 text-sm font-semibold text-terracotta">Move to next stage →</button>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-head text-left">{t("raise_complaint")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 items-start">
              <Textarea className="rounded-xl min-h-[90px]" data-testid="ticket-desc" placeholder={t("describe_issue")} value={desc} onChange={(e) => setDesc(e.target.value)} />
              <VoiceButton onResult={(txt) => setDesc((d) => (d ? d + " " : "") + txt)} testId="ticket-voice-btn" />
            </div>
            <label data-testid="ticket-photo-upload" className="gs-card border-2 border-dashed border-slate-300 flex items-center justify-center gap-2 py-4 cursor-pointer text-slate-400 text-sm"><Camera className="w-5 h-5" /> {t("add_photos")}<input type="file" accept="image/*" multiple hidden onChange={onPhotos} /></label>
            {photos.length > 0 && <div className="flex gap-2">{photos.map((p) => <img key={p.slice(0, 48)} src={p} alt="" className="w-16 h-16 rounded-lg object-cover" />)}</div>}
            <WhyBanner>Sathi AI reads your complaint, finds the likely cause and who should pay per your agreement.</WhyBanner>
            <PrimaryButton onClick={submit} disabled={busy || !desc} data-testid="raise-ticket-submit-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("submit")}</PrimaryButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
